import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Routes that ALWAYS use dark mode (entry/landing pages)
const FORCED_DARK_ROUTES = [
  '/login',
  '/registro',
  '/quiz',
  '/auditor',
  '/landing-preview',
  '/funnel-preview'
];

// Check if current path requires forced dark mode
const isForcedDarkRoute = (pathname) => {
  return FORCED_DARK_ROUTES.some(route => 
    pathname === route || pathname.startsWith(route + '/')
  );
};

// Get system preference
const getSystemPreference = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const ThemeContext = createContext({
  theme: 'light',
  isDark: false,
  toggleTheme: () => {},
  setTheme: () => {},
  useSystemTheme: () => {},
  isUsingSystem: false,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('fascinai-theme');
      // If 'system' or no preference saved, use system preference
      if (!saved || saved === 'system') {
        return getSystemPreference();
      }
      return saved;
    }
    return 'light';
  });
  
  const [isUsingSystem, setIsUsingSystem] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('fascinai-theme');
      return !saved || saved === 'system';
    }
    return true;
  });

  // Listen for system theme changes
  useEffect(() => {
    if (!isUsingSystem) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setThemeState(e.matches ? 'dark' : 'light');
    };
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Fallback for older browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [isUsingSystem]);

  // Check if we're on a forced dark route
  const isForcedDark = isForcedDarkRoute(location.pathname);
  
  // Effective theme: forced dark on certain routes, otherwise user preference
  const effectiveTheme = isForcedDark ? 'dark' : theme;
  const isDark = effectiveTheme === 'dark';

  // Apply theme to document
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [isDark]);

  // Save preference to localStorage
  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    setIsUsingSystem(false);
    localStorage.setItem('fascinai-theme', newTheme);
  };

  // Use system theme
  const useSystemTheme = () => {
    setIsUsingSystem(true);
    localStorage.setItem('fascinai-theme', 'system');
    setThemeState(getSystemPreference());
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      isDark, 
      toggleTheme, 
      setTheme,
      useSystemTheme,
      isUsingSystem,
      isForcedDark 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
